import FootballIcon from '@/assets/icons/footballIcon';
import { getActivePeriodIndex, getFinalScoreColumns, getScoreboard } from '@/feature/bets-sbk/utils/utils';
import { formatSportPeriodDetails } from '@/utils/format-sport-period-details/format-sport-period-details';
import { normalizeSportName } from '@/utils/get-sport-key';
import { ScoreboardSport, TeamSide, getTeamOrder } from '@/utils/sport-utils';

import { ScoreBoardGridProps, ScoreboardEvent, StoreCacheSlice } from '../types';
import { buildTeamInfo, cacheToEventDetails, toPeriodScore } from './build-team-info';

const AMERICAN_FOOTBALL: ScoreboardSport = 'american_football';

/**
 * Per-team possession indicator (brown football) for live American Football. `ball_possession`
 * is sport-specific to football, and the icon is football-shaped, so this is gated to the sport
 * rather than rendered for every period-score sport sharing this builder.
 */
const getFootballPossessionIndicators = (
    event: ScoreboardEvent,
    cache: StoreCacheSlice,
    firstSide: TeamSide,
    secondSide: TeamSide
): Pick<ScoreBoardGridProps, 'team1Indicator' | 'team2Indicator'> => {
    if (cache.eventState !== 'live' || normalizeSportName(event.sport.name) !== AMERICAN_FOOTBALL) {
        return {};
    }
    // ball_possession is 'HOME' | 'AWAY'; team order sides are lowercase.
    const possessionSide = cache.sportSpecific?.ball_possession?.toLowerCase();
    return {
        team1Indicator: possessionSide === firstSide ? <FootballIcon /> : undefined,
        team2Indicator: possessionSide === secondSide ? <FootballIcon /> : undefined,
    };
};

/**
 * Generic builder for sports whose scoreboard is driven by period_scores —
 * American Football, Basketball, Ice Hockey, Soccer. Delegates the period
 * grid + finals math to the existing utils in feature/bets-sbk/utils so
 * sport-specific quirks (NFL OT, in-season ties, etc.) stay in one place.
 */
export const buildPeriodScoreboardProps = (
    event: ScoreboardEvent,
    cache: StoreCacheSlice
): Omit<ScoreBoardGridProps, 'variant' | 'matchDetails'> | undefined => {
    const { periodName, sportSpecific, eventState } = cache;

    const [firstSide, secondSide] = getTeamOrder(event.sport.name);
    const { team1, team2 } = buildTeamInfo(event, firstSide, secondSide);

    if (eventState === 'pregame') {
        return { team1, team2, breakdown: [], eventState };
    }

    const eventDetails = cacheToEventDetails(event, cache);
    const grid = getScoreboard(event.sport, event.league, eventDetails);
    const finals = getFinalScoreColumns(eventDetails, event.sport);
    const isLive = eventState === 'live';
    const activePeriodIndex = isLive ? getActivePeriodIndex(grid, periodName) : -1;
    // formatSportPeriodDetails returns '' for sports without sport-specific period details
    // (basketball, hockey, soccer) — only football currently produces a non-empty string here.
    // The `|| undefined` is load-bearing: downstream UI hides the "•" row when periodDetails is
    // undefined but would render an empty separator if passed ''.
    const periodDetails = isLive ? formatSportPeriodDetails(event, sportSpecific) || undefined : undefined;

    return {
        team1,
        team2,
        ...getFootballPossessionIndicators(event, cache, firstSide, secondSide),
        breakdown: grid.map(p => toPeriodScore(p, firstSide)),
        finalScoreColumns: finals.map(p => toPeriodScore(p, firstSide)),
        activePeriodIndex: activePeriodIndex >= 0 ? activePeriodIndex : undefined,
        periodDetails,
        eventState,
    };
};
